import axios from 'axios';

// Detecta automaticamente se esta acessando via rede local ou producao
const getApiBaseUrl = () => {
  const hostname = window.location.hostname;

  if (hostname === 'aplicaiaapp.com' || hostname === 'www.aplicaiaapp.com') {
    return 'https://api.aplicaiaapp.com/api';
  }
  if (hostname === 'rag.celintelligence.com') {
    return 'https://rag-api.celintelligence.com/api';
  }
  if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
    return `http://${hostname}:8000/api`;
  }
  return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
};

const API_BASE_URL = getApiBaseUrl();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000,
});

// Interceptor de requisição — injeta o token JWT em todas as chamadas autenticadas
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('rag_auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor de resposta — redireciona para login só quando um token de sessão
// expira. NUNCA age em chamadas de autenticação (login/registro): nesses casos o
// 401/400 é resposta esperada e quem mostra o erro é a própria tela — recarregar
// a página aqui apagaria a mensagem (o bug do "login que não dá erro nenhum").
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url: string = error.config?.url || '';
    const isAuthCall = /\/auth\/(login|register|signup)/.test(url);
    const currentPath = window.location.pathname;
    const onAuthPage = currentPath === '/' || currentPath === '/register' || currentPath === '/login';

    if (error.response?.status === 401 && !isAuthCall && !onAuthPage) {
      // Token expirado/ inválido numa página autenticada — limpa sessão e reloga.
      localStorage.removeItem('rag_auth_token');
      localStorage.removeItem('rag_auth_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Types
export interface User {
  email: string;
  name: string;
  role: string;
}

export interface UserStatus {
  id: number;
  email: string;
  name: string;
  role: string;
  status: string;
  created_at: string;
  approved_at?: string;
  approved_by?: string;
}

export interface RegisterRequest {
  email: string;
  name: string;
  password: string;
}

export interface DocumentResponse {
  id: string;
  filename: string;
  status: string;
  created_at: string;
  collection_name: string;
  chunks_count?: number;
}

export interface ProcessingStatus {
  document_id: string;
  status: string;
  progress: number;
  message?: string;
  chunks_count?: number;
  statistics?: Record<string, any>;
  metadata?: Record<string, any>;
  filename?: string;
}

export interface BatchStatus {
  batch_id: string;
  total_documents: number;
  completed: number;
  failed: number;
  processing: number;
  queued: number;
  overall_progress: number;
  status: string;
  documents: Record<string, ProcessingStatus>;
}

export interface DocumentPreview {
  filename: string;
  markdown_content: string;
  metadata: Record<string, any>;
  chunks: Array<{
    text: string;
    chunk_id: number;
    metadata: Record<string, any>;
  }>;
  images: Array<{
    index: number;
    caption: string;
    extracted_text: string;
    ai_description?: string;
    vision_model?: string;
    analysis_tokens?: number;
    bbox?: any;
    page?: number;
    type?: string;
    source?: string;
  }>;
  statistics: {
    file_size_mb: number;
    pages: number;
    tables_count: number;
    figures_count: number;
    images_count: number;
    images_with_text: number;
    images_with_ai_description: number;
    chunks_count: number;
    text_length: number;
    format: string;
  };
  knowledge_graph?: {
    entities: Array<{
      name: string;
      type: string;
      description?: string;
      confidence?: number;
    }>;
    relationships: Array<{
      source: string;
      target: string;
      relationship: string;
      context?: string;
      confidence?: number;
    }>;
  };
  success: boolean;
  error?: string;
}

export interface CollectionInfo {
  name: string;
  documents_count: number;
  vectors_count: number;
  created_at?: string;
}

export interface ChatRequest {
  message: string;
  collection_names: string[];
  model: string;
  max_tokens?: number;
  temperature?: number;
  context_limit?: number;
  debug?: boolean;
}

export interface ProcessingResults {
  success: boolean;
  document: {
    document_id: string;
    filename: string;
    collection_name: string;
    status: string;
    progress: number;
    message: string;
    statistics: Record<string, any>;
    metadata: Record<string, any>;
    chunks_count: number;
    processing_time: string;
    timestamp: string;
  };
  qdrant?: {
    collection_info?: {
      name: string;
      vectors_count: number;
      indexed: string;
      config: {
        vector_size: string | number;
        distance: string;
      };
    };
    vectors_count: number;
    sample_vectors: Array<{
      id: string;
      score: number;
      metadata: Record<string, any>;
      has_text: boolean;
    }>;
    error?: string;
  };
  generated_at: string;
  data_sources?: {
    qdrant_available: boolean;
    vectors_indexed: boolean;
  };
}

export interface ChatResponse {
  response: string;
  sources: Array<{
    filename: string;
    collection: string;
    relevance_score: number;
    chunk_id: number;
    text_preview: string;
    source_file?: string;
    document_type?: string;
    file_size?: string;
    last_modified?: string;
    content_type?: string;
    processing_mode?: string;
    has_ai_analysis?: boolean;
    ai_tokens_used?: number;
    processing_date?: string;
    file_hash?: string;
    is_recent?: boolean;
    reliability_score?: number;
  }>;
  model_used: string;
  tokens_used?: number;
  performance?: {
    total_duration_seconds?: number;
    api_duration_seconds?: number;
    tokens_per_second?: number;
  };
  debug_info?: {
    request_id?: string;
    question_analysis?: {
      type: string;
      complexity: string;
    };
    context_chunks_used?: number;
    system_message_length?: number;
    timestamp?: string;
  };
  error_type?: string;
  error_message?: string;
  suggestions?: string[];
  success?: boolean;
  graph_data?: {
    entities: Array<{
      name: string;
      type: string;
      properties?: Record<string, any>;
      confidence?: number;
      document_id?: string;
      collection?: string;
    }>;
    relationships: Array<{
      source: string;
      target: string;
      relationship: string;
      context?: string;
      confidence?: number;
    }>;
    statistics?: {
      total_entities?: number;
      total_relationships?: number;
      entity_types?: Record<string, number>;
    };
  };
  agent_info?: {
    agent_used?: string;
    strategy?: string;
    confidence?: number;
    reasoning?: string;
    hybrid_results_count?: number;
    vector_results?: number;
    graph_results?: number;
    tools_used?: string[];
  };
  performance_stats?: {
    total_hybrid_results?: number;
    entities_found?: number;
    relationships_found?: number;
    collections_searched?: string[];
    search_mode?: string;
    response_enhanced?: boolean;
  };
}

// ===== Regulatório (F1) =====
// Espelha os modelos do backend em models/regulatory.py. O isolamento de tenant
// é feito no servidor (filtro derivado do token) — o cliente nunca envia tenant.

export interface RegulatoryFilters {
  agency?: string[];
  doc_type?: string[];
  status?: string | null;
  date_from?: string | null; // ISO yyyy-mm-dd
  date_to?: string | null;
  concessionarias?: string[];
}

export interface RegulatorySearchRequest {
  query: string;
  filters?: RegulatoryFilters;
  max_results?: number;
  include_revoked?: boolean;
}

export interface RegulatorySource {
  document_id?: string;
  title?: string;
  agency?: string;
  doc_type?: string;
  status?: string;
  date_issued?: string;
  page?: number;
  section?: string;
  url_original?: string;
  snippet: string;
  relevance_score?: number;
}

export interface RegulatorySearchResponse {
  answer?: string | null;
  sources: RegulatorySource[];
  audit_trail: Record<string, any>;
}

// Auth API
export const authApi = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  register: async (data: RegisterRequest) => {
    const response = await api.post('/auth/register', data);
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  me: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  changePassword: async (current_password: string, new_password: string) => {
    const response = await api.post('/auth/change-password', { current_password, new_password });
    return response.data;
  },

  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token: string, new_password: string) => {
    const response = await api.post('/auth/reset-password', { token, new_password });
    return response.data;
  },

  signup: async (data: { company: string; name: string; email: string; password: string; ref?: string }) => {
    const response = await api.post('/auth/signup', data);
    return response.data; // { success, token, user }
  },
};

// API pública (landing — sem autenticação)
export interface PublicPlan {
  id: string;
  label: string;
  price_brl: number | null;
  max_users: number;
  max_collections: number;
  max_file_mb: number | null;
  max_documents: number | null;
  connectors: string[];
  premium_models: boolean;
  models_label?: string | null;
  max_whatsapp_messages?: number | null;
  max_automations?: number | null;
  allow_audio?: boolean;
  allow_ocr?: boolean;
}
export const publicApi = {
  plans: async (): Promise<PublicPlan[]> => (await api.get('/public/plans')).data.plans,
  unsubscribeVerify: async (token: string): Promise<{ email_masked: string; opted_out: boolean }> =>
    (await api.get('/public/unsubscribe/verify', { params: { token } })).data,
  unsubscribe: async (data: { token: string; reason?: string; resubscribe?: boolean }): Promise<{ success: boolean; opted_out: boolean; message: string }> =>
    (await api.post('/public/unsubscribe', data)).data,
  contact: async (data: { name: string; email: string; company?: string; message: string; phone?: string }) =>
    (await api.post('/public/contact', data)).data,
};

// Admin API — endpoints exclusivos para role=admin
export const adminApi = {
  getAllUsers: async (): Promise<UserStatus[]> => {
    const response = await api.get('/auth/admin/users');
    return response.data;
  },

  getPendingUsers: async (): Promise<UserStatus[]> => {
    const response = await api.get('/auth/admin/users/pending');
    return response.data;
  },

  getPendingCount: async (): Promise<number> => {
    const response = await api.get('/auth/admin/users/pending/count');
    return response.data.count;
  },

  approveUser: async (email: string) => {
    const response = await api.post(`/auth/admin/users/${encodeURIComponent(email)}/approve`);
    return response.data;
  },

  rejectUser: async (email: string) => {
    const response = await api.post(`/auth/admin/users/${encodeURIComponent(email)}/reject`);
    return response.data;
  },

  deleteUser: async (email: string) => {
    const response = await api.delete(`/auth/admin/users/${encodeURIComponent(email)}`);
    return response.data;
  },

  createUser: async (data: { name: string; email: string; password?: string }): Promise<{ success: boolean; email: string; temp_password: string | null; emailed?: boolean }> => {
    const response = await api.post('/auth/admin/users', data);
    return response.data;
  },

  resetPassword: async (email: string): Promise<{ success: boolean; email: string; temp_password: string }> => {
    const response = await api.post(`/auth/admin/users/${encodeURIComponent(email)}/reset-password`);
    return response.data;
  },
};

// Documents API
export const documentsApi = {
  upload: async (file: File, collectionName: string): Promise<DocumentResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('collection_name', collectionName);
    const response = await api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  uploadBatch: async (files: File[], collectionName: string) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    formData.append('collection_name', collectionName);
    const response = await api.post('/documents/upload-batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600000,
    });
    return response.data;
  },

  getBatchStatus: async (batchId: string): Promise<BatchStatus> => {
    const response = await api.get(`/documents/batch-status/${batchId}`);
    return response.data;
  },

  getStatus: async (documentId: string): Promise<ProcessingStatus> => {
    const response = await api.get(`/documents/status/${documentId}`);
    return response.data;
  },

  getSupportedFormats: async () => {
    const response = await api.get('/documents/supported-formats');
    return response.data;
  },

  clearStatus: async (documentId: string) => {
    const response = await api.delete(`/documents/status/${documentId}`);
    return response.data;
  },

  getProcessingResults: async (documentId: string): Promise<ProcessingResults> => {
    const response = await api.get(`/documents/processing-results/${documentId}`);
    return response.data;
  },

  preview: async (file: File): Promise<DocumentPreview> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/documents/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
    return response.data;
  },

  previewBatch: async (files: File[]): Promise<DocumentPreview> => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    const response = await api.post('/documents/preview-batch', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180000,
    });
    return response.data;
  },
};

// Collections API
export const collectionsApi = {
  list: async (): Promise<CollectionInfo[]> => {
    const response = await api.get('/collections/');
    return response.data;
  },

  create: async (name: string, vectorSize: number = 1536) => {
    const response = await api.post('/collections/', { name, vector_size: vectorSize });
    return response.data;
  },

  delete: async (name: string) => {
    const response = await api.delete(`/collections/${name}`);
    return response.data;
  },

  getInfo: async (name: string) => {
    const response = await api.get(`/collections/${name}`);
    return response.data;
  },

  getStats: async (name: string) => {
    const response = await api.get(`/collections/${name}/stats`);
    return response.data;
  },

  search: async (collectionName: string, query: string, limit: number = 5) => {
    const response = await api.post(`/collections/${collectionName}/search`, null, {
      params: { query, limit },
    });
    return response.data;
  },
};

// Chat API
export const chatApi = {
  chat: async (request: ChatRequest): Promise<ChatResponse> => {
    const response = await api.post('/chat/', request);
    return response.data;
  },

  chatWithConversation: async (conversationId: string, request: ChatRequest): Promise<ChatResponse> => {
    const response = await api.post(`/chat/conversation/${conversationId}`, request);
    return response.data;
  },

  getAvailableModels: async () => {
    const response = await api.get('/chat/models');
    return response.data;
  },

  getConversationHistory: async (conversationId: string) => {
    const response = await api.get(`/chat/conversation/${conversationId}/history`);
    return response.data;
  },

  clearConversation: async (conversationId: string) => {
    const response = await api.delete(`/chat/conversation/${conversationId}`);
    return response.data;
  },

  searchOnly: async (query: string, collectionNames: string[], limit: number = 10) => {
    const response = await api.post('/chat/search-only', {
      query,
      collection_names: collectionNames,
      limit,
    });
    return response.data;
  },
};

// ===== Integrações (conectores self-service) =====
// O schema vem do backend; o wizard renderiza os campos dinamicamente.

export interface IntegrationField {
  name: string;
  label: string;
  type: string;            // text | number | password | select
  options?: string[];      // para type === 'select'
  required?: boolean;
  secret?: boolean;
  help?: string;
  column_picker?: boolean; // renderiza seletor de colunas (após Testar)
  table_picker?: boolean;  // renderiza seletor de TABELAS (após Testar)
  multi?: boolean;         // múltiplas colunas (checkboxes, valor separado por vírgula)
}

export interface IntegrationType {
  type: string;
  label: string;
  mode: string;            // form | oauth | agent | whatsapp
  instructions: string[];
  note?: string;           // nota de conectividade/segurança (callout no wizard)
  doc_url?: string;        // link direto para o console (ex.: Google/Azure)
  doc_label?: string;      // texto do botão do link
  copy_value?: { label: string; value: string }; // valor copiável (ex.: URI de redirecionamento)
  fields: IntegrationField[];
}

export interface AgentGuide {
  api_url: string;
  token: string | null;    // só vem no momento da geração; depois null
  base_name: string;
  steps: string[];
  env_example: string;
  run_command: string;
}

export interface Integration {
  id: string;
  type: string;
  name: string;
  target_collection: string;
  status: string;
  last_run?: string | null;
  last_result?: Record<string, any> | null;
  created_at?: string | null;
  sync_mode?: string;   // periodic | manual | realtime
  scoped?: boolean;     // tem pasta/prefixo definido (fontes de arquivo amplas)
  scope_label?: string; // nome amigável da pasta/prefixo selecionado
}

export const integrationsApi = {
  getTypes: async (): Promise<IntegrationType[]> => {
    const response = await api.get('/integrations/types');
    return response.data.types;
  },
  test: async (type: string, config: Record<string, any>): Promise<{ ok: boolean; message: string; columns?: string[]; tables?: string[] }> => {
    const response = await api.post('/integrations/test', { type, config });
    return response.data;
  },
  create: async (payload: {
    type: string;
    name: string;
    target_collection: string;
    config: Record<string, any>;
    sync_mode?: string;
    sync_interval_minutes?: number;
  }): Promise<{ id: string; status: string; agent_token?: string; agent_guide?: AgentGuide }> => {
    const response = await api.post('/integrations/', payload);
    return response.data;
  },
  // Agente Local: guia de instalação (sem token) e geração de novo token.
  agentGuide: async (id: string): Promise<{ guide: AgentGuide; has_token: boolean }> => {
    const response = await api.get(`/integrations/${id}/agent`);
    return response.data;
  },
  regenerateAgentToken: async (id: string): Promise<{ agent_token: string; agent_guide: AgentGuide }> => {
    const response = await api.post(`/integrations/${id}/agent/regenerate`);
    return response.data;
  },
  // Google Drive: seletor de Drive Compartilhado + pasta (pós-conexão).
  driveFolders: async (id: string, driveId = ''): Promise<{
    shared_drives: { id: string; name: string }[];
    folders: { id: string; name: string }[];
    current_drive_id: string;
  }> => {
    const response = await api.get(`/integrations/${id}/drive/folders`, { params: { drive_id: driveId } });
    return response.data;
  },
  setDriveScope: async (id: string, payload: { drive_id: string; folder_id: string; folder_name?: string }): Promise<{ status: string }> => {
    const response = await api.put(`/integrations/${id}/drive/scope`, payload);
    return response.data;
  },
  // Config do Google Picker (token fresco + api key + app id) — drive.file.
  drivePickerConfig: async (id: string): Promise<{ access_token: string; api_key: string; app_id: string }> => {
    const response = await api.get(`/integrations/${id}/drive/picker-config`);
    return response.data;
  },
  // Config para pré-preencher a edição (segredos vêm em branco, por segurança).
  getConfig: async (id: string): Promise<{
    type: string;
    name: string;
    target_collection: string;
    config: Record<string, any>;
  }> => {
    const response = await api.get(`/integrations/${id}/config`);
    return response.data;
  },
  update: async (
    id: string,
    payload: { name: string; config: Record<string, any>; sync_mode?: string; sync_interval_minutes?: number },
  ): Promise<{ id: string; status: string }> => {
    const response = await api.put(`/integrations/${id}`, payload);
    return response.data;
  },
  list: async (): Promise<Integration[]> => {
    const response = await api.get('/integrations/');
    return response.data.integrations;
  },
  run: async (id: string): Promise<{ success: boolean; result?: Record<string, any>; queued?: boolean }> => {
    const response = await api.post(`/integrations/${id}/run`);
    return response.data;
  },
  startOAuth: async (id: string): Promise<{ authorize_url: string }> => {
    const response = await api.get(`/integrations/${id}/oauth/start`);
    return response.data;
  },
  // WhatsApp (Evolution) — fluxo de conexão self-service
  whatsappConnect: async (id: string): Promise<{ instance: string; qrcode: string | null }> => {
    const response = await api.post(`/integrations/${id}/whatsapp/connect`);
    return response.data;
  },
  whatsappState: async (id: string): Promise<{ state: string }> => {
    const response = await api.get(`/integrations/${id}/whatsapp/state`);
    return response.data;
  },
  whatsappChats: async (id: string): Promise<{ chats: Array<{ id: string; name: string }> }> => {
    const response = await api.get(`/integrations/${id}/whatsapp/chats`);
    return response.data;
  },
  whatsappSaveChats: async (
    id: string,
    selected: string[],
    labels: Record<string, string> = {},
  ): Promise<{ success: boolean; count: number }> => {
    const response = await api.put(`/integrations/${id}/whatsapp/chats`, {
      selected_chats: selected,
      chat_labels: labels,
    });
    return response.data;
  },
  remove: async (id: string) => {
    const response = await api.delete(`/integrations/${id}`);
    return response.data;
  },
  runs: async (id: string): Promise<Array<{ status: string; indexed: number; detail: string; created_at: string | null }>> =>
    (await api.get(`/integrations/${id}/runs`)).data.runs,
};

// ===== Grupos (RBAC) =====
export interface Group {
  id: string;
  name: string;
  members: number;
  collections: number;
}
export interface GroupOptions {
  users: Array<{ email: string; name: string }>;
  collections: string[];
}
export interface GroupDetail {
  members: string[];
  collections: string[];
}
export const groupsApi = {
  list: async (): Promise<Group[]> => (await api.get('/groups/')).data.groups,
  options: async (): Promise<GroupOptions> => (await api.get('/groups/options')).data,
  create: async (name: string): Promise<{ id: string }> => (await api.post('/groups/', { name })).data,
  detail: async (id: string): Promise<GroupDetail> => (await api.get(`/groups/${id}`)).data,
  setMembers: async (id: string, emails: string[]) =>
    (await api.put(`/groups/${id}/members`, { emails })).data,
  setCollections: async (id: string, collections: string[]) =>
    (await api.put(`/groups/${id}/collections`, { collections })).data,
  remove: async (id: string) => (await api.delete(`/groups/${id}`)).data,
};

// ===== Notificações (sino do admin) =====
export interface AppNotification {
  id: number;
  level: string;
  title: string;
  body?: string;
  category?: string;
  read: boolean;
  created_at?: string;
}
export const notificationsApi = {
  list: async (): Promise<{ notifications: AppNotification[]; unread: number }> => {
    const response = await api.get('/notifications/');
    return response.data;
  },
  unreadCount: async (): Promise<number> => {
    const response = await api.get('/notifications/unread-count');
    return response.data.unread;
  },
  markRead: async (id: number) => {
    const response = await api.post(`/notifications/${id}/read`);
    return response.data;
  },
  markAllRead: async () => {
    const response = await api.post('/notifications/read-all');
    return response.data;
  },
};

// ===== Automações (gatilho → IA → entrega) =====
export interface Automation {
  id: string;
  name: string;
  trigger_type: string;
  target_collection: string;
  prompt: string;
  delivery_type: string;
  delivery_target: string;
  enabled: boolean;
  last_run_at: string | null;
  last_status: string | null;
  trigger_config?: { chats?: string[]; keywords?: string[]; cadence?: string; hour?: number; minute?: number; weekday?: number; token?: string; device_id?: string; metric?: string; operator?: string; threshold?: number; unit?: string };
}
export interface WhatsappConversation { jid: string; label: string; integration: string }
export interface AutomationRun { source: string; status: string; input_preview: string; output_preview: string; created_at: string | null }
export const iotApi = {
  config: async (): Promise<{ token: string; url: string }> => (await api.get('/iot/config')).data,
  readings: async () => (await api.get('/iot/readings')).data.readings,
};
export const automationsApi = {
  list: async (): Promise<Automation[]> => (await api.get('/automations/')).data.automations,
  create: async (data: {
    name: string; trigger_type: string; target_collection: string; prompt: string;
    delivery_type: string; delivery_target: string; delivery_instance?: string;
    trigger_config?: { chats?: string[]; keywords?: string[] };
  }) => (await api.post('/automations/', data)).data,
  toggle: async (id: string, enabled: boolean) =>
    (await api.put(`/automations/${id}/enabled?enabled=${enabled}`)).data,
  run: async (id: string) => (await api.post(`/automations/${id}/run`)).data,
  remove: async (id: string) => (await api.delete(`/automations/${id}`)).data,
  whatsappConversations: async (): Promise<WhatsappConversation[]> =>
    (await api.get('/automations/whatsapp-conversations')).data.conversations,
  runs: async (id: string): Promise<AutomationRun[]> => (await api.get(`/automations/${id}/runs`)).data.runs,
};

// ===== Billing (Asaas) =====
export interface BillingInfo {
  status: string;
  plan?: string;
  customer_id?: string;
  subscription_id?: string;
}
export const billingApi = {
  me: async (): Promise<{ billing: BillingInfo; configured: boolean }> => (await api.get('/billing/me')).data,
  subscribe: async (plan: string, cpf_cnpj: string): Promise<{ success: boolean; payment_link: string }> =>
    (await api.post('/billing/subscribe', { plan, cpf_cnpj })).data,
  cancel: async (): Promise<{ success: boolean; message: string }> => (await api.post('/billing/cancel')).data,
  trial: async (): Promise<{ is_trial: boolean; ends_at: string | null; days_left: number; expired: boolean }> =>
    (await api.get('/billing/trial')).data,
};

// ===== Painel do Operador (plataforma) =====
export interface TenantEconomics {
  revenue_brl: number;
  ai_cost_brl: number;
  storage_cost_brl: number;
  total_cost_brl: number;
  margin_pct: number | null;
}
export interface OperatorTenant {
  tenant_id: string;
  name: string;
  created_at: string | null;
  plan: string;
  status: string;
  users: number;
  chunks: number;
  ai_spend_month: number;
  last_activity: string | null;
  economics?: TenantEconomics;
}
export interface OperatorData {
  metrics: {
    total: number; trials: number; paying: number; mrr_brl: number; ai_spend_month_usd: number;
    variable_cost_brl: number; infra_cost_brl: number; gross_profit_brl: number; gross_margin_pct: number | null;
  };
  tenants: OperatorTenant[];
}
export interface GrowthData {
  signups: { total: number; last_7d: number; last_30d: number };
  funnel: { signups: number; activated: number; paying: number };
  emails: { welcome: number; activation: number; upgrade: number; total: number };
  signups_series: { day: string; count: number }[];
}
export const operatorApi = {
  growth: async (): Promise<GrowthData> => (await api.get('/operator/growth')).data,
  access: async (): Promise<boolean> => (await api.get('/operator/access')).data.is_platform_admin,
  tenants: async (): Promise<OperatorData> => (await api.get('/operator/tenants')).data,
};

export interface ReferralStats {
  code: string;
  link: string;
  count: number;
  bonus_documents: number;
  bonus_per_referral: number;
}
export const referralsApi = {
  me: async (): Promise<ReferralStats> => (await api.get('/referrals/me')).data,
};

// ===== Onboarding / ativação =====
export interface OnboardingStatus {
  has_base: boolean;
  has_knowledge: boolean;
  has_question: boolean;
  has_source: boolean;
  sample_exists: boolean;
  completed: number;
  total: number;
}
export interface SampleBaseResponse {
  success: boolean;
  display_name: string;
  already_existed: boolean;
  suggested_questions: string[];
  message: string;
}
export const onboardingApi = {
  status: async (): Promise<OnboardingStatus> => (await api.get('/onboarding/status')).data,
  loadSampleBase: async (): Promise<SampleBaseResponse> => (await api.post('/onboarding/sample-base')).data,
};

// ===== Governança de uso de IA =====
export interface UsageRow {
  key: string;
  calls: number;
  tokens: number;
  cost_usd: number;
}
export interface ProcessModelRow {
  process: string;
  model: string;
  calls: number;
  tokens: number;
  cost_usd: number;
}
export interface UsageSummary {
  days: number;
  by_process: UsageRow[];
  by_user: UsageRow[];
  by_model: UsageRow[];
  by_group: UsageRow[];
  by_process_model: ProcessModelRow[];
  total: { calls: number; tokens: number; cost_usd: number };
}
export interface OpenRouterBalance {
  total: number | null;
  used: number | null;
  remaining: number | null;
}
export interface PlanStatus {
  plan: string;
  label: string;
  limits: { max_users: number; max_collections: number; monthly_ai_budget_usd: number; connectors: string[]; max_file_mb?: number; max_documents?: number };
  usage: { users: number; collections: number; documents?: number; ai_spend_month_usd: number };
  ai_within_budget: boolean;
  trial: { is_trial: boolean; ends_at: string | null; days_left: number; expired: boolean };
  locked: boolean;
}
export const usageApi = {
  summary: async (days: number = 30): Promise<UsageSummary> => {
    const response = await api.get(`/usage/summary?days=${days}`);
    return response.data;
  },
  plan: async (): Promise<PlanStatus> => {
    const response = await api.get('/usage/plan');
    return response.data;
  },
  balance: async (): Promise<OpenRouterBalance> => {
    const response = await api.get('/usage/balance');
    return response.data;
  },
};

// Regulatório API (F1) — busca regulatória com citação granular
export const regulatoryApi = {
  search: async (request: RegulatorySearchRequest): Promise<RegulatorySearchResponse> => {
    const response = await api.post('/regulatory/search', request);
    return response.data;
  },

  getAgencies: async (): Promise<string[]> => {
    const response = await api.get('/regulatory/agencies');
    return response.data;
  },

  getDocTypes: async (): Promise<string[]> => {
    const response = await api.get('/regulatory/doc-types');
    return response.data;
  },

  getThemes: async (): Promise<string[]> => {
    const response = await api.get('/regulatory/themes');
    return response.data;
  },
};

export default api;
